import { Node } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { Attachment as AttachmentType } from '../../@types/index.js'

import { AttachmentNodeView } from './AttachmentNodeView.js'

export interface AttachmentOptions {
  attachments: AttachmentType[]
  HTMLAttributes: Record<string, unknown>
}

interface AttachmentAttr {
  attachment?: AttachmentType
  uploadId?: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    attachment: {
      /**
       * Add an attachment
       */
      setAttachment: (options: AttachmentAttr) => ReturnType
    }
  }
}

export const Attachment = Node.create<AttachmentOptions>({
  name: 'attachment',

  inline: false,
  group: 'block',

  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      attachments: [],
    }
  },

  addAttributes() {
    return {
      attachment: {
        default: undefined,
        rendered: false,
      },
      id: {
        default: undefined,
        rendered: false,
      },
      uploadId: {
        default: undefined,
        parseHTML: element => element.getAttribute('data-upload-id'),
        renderHTML: (attributes: AttachmentAttr) => {
          const { uploadId } = attributes
          return {
            'data-upload-id': uploadId,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'attachment[data-type=attachment]',
        getAttrs: dom => {
          if (typeof dom === 'string') {
            return {}
          }
          const element = dom as HTMLImageElement
          const id = element.getAttribute('data-id')
          const attachment = this.options.attachments?.find(e => e.id === id)

          if (!attachment && !id) {
            return {}
          }

          return {
            id,
            attachment,
          }
        },
      },
    ]
  },

  renderHTML({ node }) {
    const { attachment, id } = node.attrs
    if (!attachment && !id) {
      return ['div']
    }
    return [
      'attachment',
      {
        'data-type': 'attachment',
        'data-id': id || attachment?.id,
      },
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AttachmentNodeView)
  },

  addCommands() {
    return {
      setAttachment:
        attrs =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          })
        },
    }
  },
})
